import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  calculateEmbedDimensions,
  FIXED_DESKTOP_EMBED_WIDTH,
  shouldPreserveEmbedDimensions
} from '../../util/iframeViewport';

export class ResponsiveEmbed extends Component {
  constructor(props) {
    super(props);
    this.state = {
      iframeHeight: 0,
      measured: false,
      scale: 1
    };
    this.container = null;
    this.lastContainerWidth = 0;
    this.measurementFrame = null;
    this.resizeObserver = null;
    this.visualViewport = null;
    this.supportsCssZoom = typeof window !== 'undefined' &&
      window.CSS &&
      typeof window.CSS.supports === 'function' &&
      window.CSS.supports('zoom', '1');
  }

  componentDidMount() {
    this.visualViewport = window.visualViewport || null;
    window.addEventListener('resize', this.scheduleMeasurement);
    window.addEventListener('orientationchange', this.scheduleMeasurement);
    if (this.visualViewport) {
      this.visualViewport.addEventListener('resize', this.scheduleMeasurement);
    }
    if (window.ResizeObserver) {
      this.resizeObserver = new window.ResizeObserver(this.scheduleMeasurement);
      if (this.container) {
        this.resizeObserver.observe(this.container);
      }
    }
    this.measure();
    this.scheduleMeasurement();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.scheduleMeasurement);
    window.removeEventListener('orientationchange', this.scheduleMeasurement);
    if (this.visualViewport) {
      this.visualViewport.removeEventListener('resize', this.scheduleMeasurement);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.measurementFrame !== null) {
      window.cancelAnimationFrame(this.measurementFrame);
    }
  }

  setContainer = (element) => {
    if (this.container && this.resizeObserver) {
      this.resizeObserver.unobserve(this.container);
    }

    this.container = element;

    if (element && this.resizeObserver) {
      this.resizeObserver.observe(element);
    }
    if (element) {
      this.scheduleMeasurement();
    }
  }

  scheduleMeasurement = () => {
    if (this.measurementFrame !== null) {
      return;
    }

    this.measurementFrame = window.requestAnimationFrame(() => {
      this.measurementFrame = null;
      this.measure();
    });
  }

  measure = () => {
    if (!this.container) {
      return;
    }

    const containerWidth = this.container.clientWidth;
    const containerHeight = this.container.clientHeight;
    const visualViewportScale = this.visualViewport ? this.visualViewport.scale : 1;

    if (shouldPreserveEmbedDimensions(
      visualViewportScale,
      containerWidth,
      this.lastContainerWidth
    )) {
      return;
    }

    const dimensions = calculateEmbedDimensions(containerWidth, containerHeight);
    if (!dimensions) {
      return;
    }

    this.lastContainerWidth = containerWidth;
    if (this.state.measured &&
        this.state.iframeHeight === dimensions.height &&
        this.state.scale === dimensions.scale) {
      return;
    }

    this.setState({
      iframeHeight: dimensions.height,
      measured: true,
      scale: dimensions.scale
    });
  }

  render() {
    const { src, title } = this.props;
    const { iframeHeight, measured, scale } = this.state;
    const iframeStyle = {
      width: `${FIXED_DESKTOP_EMBED_WIDTH}px`,
      height: `${iframeHeight || 1}px`,
      visibility: measured ? 'visible' : 'hidden',
      zoom: this.supportsCssZoom ? String(scale) : '1',
      transform: this.supportsCssZoom ? 'none' : `scale(${scale})`
    };

    return (
      <div className="responsive-embed" ref={this.setContainer}>
        <iframe
          src={src}
          style={iframeStyle}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          scrolling="yes"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-presentation"
        />
      </div>
    );
  }
}

ResponsiveEmbed.propTypes = {
  src: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired
};

export default ResponsiveEmbed;
